import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from '@/config/dayjs';
import React from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import { outlinedInputRootSx } from '@/constants/dimensions';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  US_STATE_OPTIONS,
} from './constants';
import {
  useCreateSetupInvestorMutation,
  useCreateHouseholdMutation,
  useGetAdvisorAccountsForInvestorsQuery,
} from '@/store/clientsApi';

type AddSpousePartnerModalProps = {
  open: boolean;
  onClose: () => void;
  primaryInvestorId: number;
  primaryInvestorName: string;
};

const AddSpousePartnerModal: React.FC<AddSpousePartnerModalProps> = ({
  open,
  onClose,
  primaryInvestorId,
  primaryInvestorName,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Step management
  const [activeStep, setActiveStep] = React.useState<number>(0);
  const steps = [
    t('enterSpousePartnerInformation', 'Enter spouse/partner information'),
    t('shareAccounts', 'Share accounts'),
  ];

  // Form state (Step 1)
  const validationSchema = React.useMemo(
    () =>
      yup.object({
        firstName: yup.string().trim().required('Required'),
        lastName: yup.string().trim(),
        email: yup.string().trim().email('Invalid email').required('Required'),
        maritalStatus: yup.mixed<number | ''>().oneOf(['', 1, 2, 3, 4, 5]),
        gender: yup.mixed<number | ''>().oneOf(['', 0, 1, 2, 3]),
        dob: yup.mixed().nullable(),
        stateCode: yup.string().trim(),
      }),
    [],
  );

  const formik = useFormik({
    validateOnMount: true,
    validationSchema,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      maritalStatus: '' as number | '',
      gender: '' as number | '',
      dob: null as Dayjs | null,
      stateCode: '',
    },
    onSubmit: () => undefined,
  });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // Created spouse investor id (from Step 1)
  const [spouseInvestorId, setSpouseInvestorId] = React.useState<number | null>(
    null,
  );

  const [createInvestor, { isLoading: isCreating }] =
    useCreateSetupInvestorMutation();
  const [createHousehold, { isLoading: isFinishing }] =
    useCreateHouseholdMutation();

  // Accounts (Step 2)
  const [selectionMode, setSelectionMode] = React.useState<
    'all' | 'choose' | null
  >(null);
  const { data: accounts = [] } = useGetAdvisorAccountsForInvestorsQuery(
    { investorIds: [primaryInvestorId] },
    { skip: !open || activeStep !== 1 },
  );
  const [excludedAccountIds, setExcludedAccountIds] = React.useState<number[]>(
    [],
  );

  const resetState = () => {
    setActiveStep(0);
    formik.resetForm();
    setSelectionMode(null);
    setExcludedAccountIds([]);
    setSpouseInvestorId(null);
    setErrorMessage(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const canContinue = () => formik.isValid;

  const handleCreateSpouse = async () => {
    setErrorMessage(null);
    try {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        formik.setTouched(
          Object.keys(errors).reduce(
            (acc, key) => Object.assign(acc, { [key]: true }),
            {} as Record<string, boolean>,
          ),
          true,
        );
        return;
      }
      const payload = {
        FirstName: formik.values.firstName.trim(),
        LastName: formik.values.lastName.trim() || null,
        UserName: formik.values.email.trim(),
        SocialSecurityNumber: null,
        DOB: formik.values.dob ? formik.values.dob.format('M/D/YYYY') : null,
        State: formik.values.stateCode || null,
        Gender:
          formik.values.gender === '' ? null : Number(formik.values.gender),
        MaritalStatus:
          formik.values.maritalStatus === ''
            ? null
            : Number(formik.values.maritalStatus),
        Name: `${formik.values.firstName}$${''} ${''}`.trim(),
      };
      // Correct name build without accidental extra spaces
      payload.Name = `${formik.values.firstName}$${''}`; // placeholder to satisfy TS then reassign
      payload.Name = `${formik.values.firstName}${
        formik.values.lastName ? ' ' + formik.values.lastName : ''
      }`;
      const created = await createInvestor(payload).unwrap();
      const id = Number((created as any)?.InvestorId);
      if (!Number.isFinite(id)) throw new Error('Invalid spouse id');
      setSpouseInvestorId(id);
      setActiveStep(1);
    } catch (err: any) {
      const msg = (err?.data?.Message || err?.message || String(err)) as string;
      setErrorMessage(msg);
    }
  };

  const handleFinish = async () => {
    if (!spouseInvestorId) return;
    try {
      const shouldChoose = selectionMode === 'choose';
      const excluded = shouldChoose ? excludedAccountIds : [];
      await createHousehold({
        Name: `${primaryInvestorName} Household`,
        Investors: [
          { InvestorId: Number(primaryInvestorId), Name: primaryInvestorName },
          {
            InvestorId: Number(spouseInvestorId),
            Name: `${formik.values.firstName}${
              formik.values.lastName ? ' ' + formik.values.lastName : ''
            }`,
          },
        ],
        PrimaryInvestorId: Number(primaryInvestorId),
        SecondaryInvestorId: Number(spouseInvestorId),
        DisplaySetting: 1,
        ExcludedAccounts: excluded,
      }).unwrap();
      handleClose();
    } catch (err: any) {
      const msg = (err?.data?.Message || err?.message || String(err)) as string;
      setErrorMessage(msg);
    }
  };

  const Title = (
    <Typography
      variant='h5'
      sx={{ fontSize: '1.25rem' }}
    >
      {t('addSpousePartner', 'Add Spouse/Partner')}
    </Typography>
  );

  const StepHeader = (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      sx={{ px: 1 }}
    >
      {steps.map((label, idx) => (
        <Step key={idx}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );

  const Step1Content = (
    <Card elevation={0}>
      <CardContent>
        <Typography
          variant='h6'
          sx={{ mb: 2 }}
        >
          {t('personalInformation', 'Personal Information')}
        </Typography>
        <Stack spacing={2}>
          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name='firstName'
                label={`${t('firstName', 'First Name')}`}
                placeholder={t('firstName', 'First Name')}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={
                  formik.touched.firstName &&
                  typeof formik.errors.firstName === 'string'
                    ? formik.errors.firstName
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name='lastName'
                label={`${t('lastName', 'Last Name')}`}
                placeholder={t('lastName', 'Last Name')}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={
                  formik.touched.lastName &&
                  typeof formik.errors.lastName === 'string'
                    ? formik.errors.lastName
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name='email'
                label={`${t('usernameEmailAddress', 'Username (Email Address)')}`}
                placeholder={t(
                  'usernameEmailAddress',
                  'Username (Email Address)',
                )}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type='email'
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  formik.touched.email &&
                  typeof formik.errors.email === 'string'
                    ? formik.errors.email
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                name='maritalStatus'
                label={t('maritalStatus', 'Marital Status')}
                value={
                  formik.values.maritalStatus === ''
                    ? ''
                    : Number(formik.values.maritalStatus)
                }
                onChange={(e) => {
                  const raw = e.target.value as string | number;
                  const str = String(raw);
                  formik.setFieldValue(
                    'maritalStatus',
                    str === '' ? '' : Number(str),
                    true,
                  );
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string | number;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t('maritalStatusSelect', 'Select')}
                        </Typography>
                      );
                    }
                    const id = Number(raw);
                    const opt = MARITAL_STATUS_OPTIONS.find((m) => m.id === id);
                    return opt ? opt.label : '';
                  },
                }}
                sx={outlinedInputRootSx}
              >
                <MenuItem value=''>
                  {t('maritalStatusSelect', 'Select')}
                </MenuItem>
                {MARITAL_STATUS_OPTIONS.map((m) => (
                  <MenuItem
                    key={m.id}
                    value={m.id}
                  >
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                name='gender'
                label={t('gender', 'Gender')}
                value={
                  formik.values.gender === ''
                    ? ''
                    : Number(formik.values.gender)
                }
                onChange={(e) => {
                  const raw = e.target.value as string | number;
                  const str = String(raw);
                  formik.setFieldValue(
                    'gender',
                    str === '' ? '' : Number(str),
                    true,
                  );
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string | number;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t('selectGender', 'Select Gender')}
                        </Typography>
                      );
                    }
                    const id = Number(raw);
                    const opt = GENDER_OPTIONS.find((g) => g.id === id);
                    return opt ? opt.label : '';
                  },
                }}
                sx={outlinedInputRootSx}
              >
                <MenuItem value=''>
                  {t('selectGender', 'Select Gender')}
                </MenuItem>
                {GENDER_OPTIONS.map((g) => (
                  <MenuItem
                    key={g.id}
                    value={g.id}
                  >
                    {g.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label={t('dateOfBirth', 'Date of Birth')}
                value={formik.values.dob}
                onChange={(d) => formik.setFieldValue('dob', d, true)}
                enableAccessibleFieldDOMStructure={!isCapacitorApp()}
                slotProps={{
                  textField: {
                    name: 'dob',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    sx: outlinedInputRootSx,
                    inputProps: isCapacitorApp()
                      ? { readOnly: true }
                      : undefined,
                    error: formik.touched.dob && Boolean(formik.errors.dob),
                    helperText:
                      formik.touched.dob &&
                      typeof formik.errors.dob === 'string'
                        ? formik.errors.dob
                        : undefined,
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={3}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                name='stateCode'
                label={t('state', 'State')}
                value={formik.values.stateCode}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t('selectState', 'Select State')}
                        </Typography>
                      );
                    }
                    const opt = US_STATE_OPTIONS.find((s) => s.code === raw);
                    return opt ? opt.name : '';
                  },
                }}
                sx={outlinedInputRootSx}
              >
                <MenuItem value=''>{t('selectState', 'Select State')}</MenuItem>
                {US_STATE_OPTIONS.map((s) => (
                  <MenuItem
                    key={s.code}
                    value={s.code}
                  >
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box />
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );

  const Step2Content = (
    <Stack spacing={2}>
      <Typography variant='h6'>
        {t('shareAccounts', 'Share accounts')}
      </Typography>
      {selectionMode === null ? (
        <Stack gap={1.5}>
          <Typography variant='body1'>
            {t(
              'household.accountsRecommend',
              'This is recommended and will reassign the accounts to the household. You can always change account assignment later if you change your mind.',
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant='contained'
              onClick={() => setSelectionMode('all')}
            >
              {t('household.shareAll', 'Yes, share all accounts')}
            </Button>
            <Button
              variant='outlined'
              onClick={() => setSelectionMode('choose')}
            >
              {t(
                'household.chooseWhich',
                'No, I want to choose which accounts get shared',
              )}
            </Button>
          </Box>
        </Stack>
      ) : null}

      {selectionMode === 'choose' ? (
        <Card variant='outlined'>
          <CardContent>
            <Typography
              variant='subtitle1'
              sx={{ mb: 1 }}
            >
              {t(
                'household.chooseAccountsTitle',
                'Choose Which Accounts You Wish To Share',
              )}
            </Typography>
            <Stack>
              {accounts.map((acct: any) => {
                const excluded = excludedAccountIds.includes(
                  Number(acct.AccountId),
                );
                return (
                  <Box
                    key={acct.AccountId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                    }}
                  >
                    <Typography>{acct.Name}</Typography>
                    <Button
                      size='small'
                      variant={excluded ? 'outlined' : 'contained'}
                      onClick={() => {
                        setExcludedAccountIds((prev) => {
                          const id = Number(acct.AccountId);
                          const has = prev.includes(id);
                          if (has) return prev.filter((x) => x !== id);
                          return [...prev, id];
                        });
                      }}
                    >
                      {excluded
                        ? t('exclude', 'Exclude')
                        : t('include', 'Include')}
                    </Button>
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='md'
      fullScreen={isMobile}
      PaperProps={{ elevation: 0, sx: { boxShadow: 'none' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {Title}
        <IconButton
          onClick={handleClose}
          aria-label={t('close', 'Close')}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>{StepHeader}</Box>
        {errorMessage ? (
          <Box sx={{ my: 2 }}>
            <Alert
              severity='error'
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          </Box>
        ) : null}
        <Box sx={{ pt: 3 }}>
          {activeStep === 0 ? Step1Content : Step2Content}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant='outlined'
            onClick={handleClose}
          >
            {t('cancel', 'Cancel')}
          </Button>
          {activeStep === 0 ? (
            <Button
              variant='contained'
              onClick={handleCreateSpouse}
              disabled={!canContinue() || isCreating}
            >
              {t('next', 'Next')}
            </Button>
          ) : (
            <Button
              variant='contained'
              onClick={handleFinish}
              disabled={isFinishing || selectionMode === null}
            >
              {t('finish', 'Finish')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddSpousePartnerModal;
